import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';

/**
 * useStableValue
 *
 * A hook that returns a stable value and a function to update it.
 *
 * Allows storing objects in state, with an equality comparison to prevent state
 * changes when the new value has the same content as the previous value.
 */

export function useStableValue<T>(initialValue: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState(initialValue);

  const setValueIfChanged = useCallback((update: SetStateAction<T>) => {
    setValue(prev => {
      const updatedValue = update instanceof Function ? update(prev) : update;
      if (isEqual(prev, updatedValue)) {
        return prev;
      } else {
        return updatedValue;
      }
    });
  }, []);

  return [value, setValueIfChanged];
}
